import CoronavirusOutlinedIcon from '@mui/icons-material/CoronavirusOutlined';
import { ReactElement, ReactSVGElement } from 'react';
import z from 'zod';

import { getHospitalName } from '@src/components/AppointmentSelection/AppointmentSelection.helpers';
import { CustomAttributeKeys } from '@src/constants';
import EmergencyOutlinedIcon from '@src/icons/EmergencyOutlinedIcon';
import { Location, LocationHours } from '@src/lib/__generated/vetspire/sdk';
import { ReasonForVisitType } from '@src/store/modules/reason-for-visit.store';
import { findCustomLocationAttribute } from '@src/utils/find-custom-location-attribute';

export enum AppointmentName {
	Sick = 'Sick',
	Urgent = 'Urgent',
	PreventativeCare = 'Preventative Care (30)',
}

export enum OutsideAppointmentName {
	TechAppt = 'Tech Appt',
	VaccineBooster = 'Vaccine Booster (20)',
	Grooming = 'Grooming',
}

export enum DisableLocationSelectAppointments {
	TechAppt = 'Tech Appt',
	VaccineBooster = 'Vaccine Booster (20)',
}

export const AppointmentTypeName = {
	[AppointmentName.Sick]: 'Sick Needs',
	[AppointmentName.Urgent]: 'Urgent Needs',
	[ReasonForVisitType.RoutineCheckUp]: 'Routine Check Up',
	[AppointmentName.PreventativeCare]: 'Routine Check Up',
	[OutsideAppointmentName.TechAppt]: 'Tech Appt',
	[OutsideAppointmentName.VaccineBooster]: 'Vaccine Booster',
	[OutsideAppointmentName.Grooming]: 'Grooming',
};

export const groupedApptTypesByReasonForVisit = {
	[ReasonForVisitType.RoutineCheckUp]: [AppointmentName.PreventativeCare],
	[ReasonForVisitType.SickNeeds]: [AppointmentName.Sick],
};

export const AppointmentNamesToReasonForVisitTypes = {
	[AppointmentName.Urgent]: ReasonForVisitType.SickNeeds,
	[AppointmentName.Sick]: ReasonForVisitType.SickNeeds,
	[AppointmentName.PreventativeCare]: [ReasonForVisitType.RoutineCheckUp],
};

interface IInfoBlockData {
	icon: ReactElement<ReactSVGElement | null>;
	title: string;
	description: string;
	subTitle: string;
	phone: string;
	infoTooltip?: string;
}

export enum dayRangeMap {
	Sunday = 'sundayRanges',
	Monday = 'mondayRanges',
	Tuesday = 'tuesdayRanges',
	Wednesday = 'wednesdayRanges',
	Thursday = 'thursdayRanges',
	Friday = 'fridayRanges',
	Saturday = 'saturdayRanges',
}

function getCurrentDayAndTimeAsIntegerInTimeZone(timeZone: string): { day: string; time: number } {
	const options: Intl.DateTimeFormatOptions = {
		weekday: 'long',
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
		timeZone: timeZone,
	};

	const formatter = new Intl.DateTimeFormat('en-US', options);
	const parts = formatter.formatToParts(new Date());

	const dayPart = parts.find((part) => part.type === 'weekday');
	const hours = Number.parseInt(parts.find((part) => part.type === 'hour')?.value || '0');
	const minutes = Number.parseInt(parts.find((part) => part.type === 'minute')?.value || '0');

	return { day: dayPart?.value || '', time: hours * 60 + minutes };
}

const checkFacilityOpenStatus = (locationHours: LocationHours, timeZone: string) => {
	const { day, time } = getCurrentDayAndTimeAsIntegerInTimeZone(timeZone);
	const currentLocationHoursRanges = locationHours[dayRangeMap[day]];
	return currentLocationHoursRanges?.some((range) => time > range[0] && time < range[1]) ?? false;
};

export const getContacts = (facility: Location | null): IInfoBlockData[] => {
	const isTeletriageEnabled = hasTeletriageEnabled(facility);
	const locationHours = facility?.locationHours;
	const timeZone = facility?.timezone;

	const isFacilityOpen =
		locationHours != null && timeZone != null && checkFacilityOpenStatus(locationHours, timeZone);
	const defaultTitle = getHospitalName(facility);
	const defaultSubTitle = isTeletriageEnabled ? '24/7 support, 100% free' : '';
	const defaultDescription =
		'We’re here for you, no matter what. Call us for a free care assessment + advice on next steps, anytime.';

	const afterHoursContactString = facility?.attributes?.find(
		(attribute) => attribute?.customAttribute?.name === 'After-Hours Referral'
	)?.value;
	const afterHoursPhone = afterHoursContactString?.split(';')?.[1] ?? '';
	const facilityTitle = isFacilityOpen || isTeletriageEnabled ? defaultTitle : 'Leave us a message';
	const facilityPhone = facility?.phoneNumber ?? afterHoursPhone;

	const facilitySubTitle = (() => {
		if (isTeletriageEnabled) return defaultSubTitle;
		return isFacilityOpen ? 'Available during business hours' : "We'll get back to you as soon as we can.";
	})();

	const facilityDescription = (() => {
		if (isTeletriageEnabled) return defaultDescription;
		return isFacilityOpen
			? 'Not sure if it’s urgent? Call our front desk for a free care assessment + advice on next steps.'
			: 'Our hospital is currently closed. Leave a message at our after-hours number, or call your preferred emergency provider.';
	})();

	return [
		{
			icon: <EmergencyOutlinedIcon size="34px" sx={{ color: 'backgrounds.lightThree' }} />,
			title: facilityTitle,
			subTitle: facilitySubTitle,
			description: facilityDescription,
			phone: facilityPhone,
		},
		{
			icon: <CoronavirusOutlinedIcon fontSize="large" sx={{ color: 'backgrounds.lightThree' }} />,
			title: 'ASPCA Poison Control',
			subTitle: 'Fees may apply',
			description: 'Get 24/7 help from experts trained in poison-related emergencies.',
			phone: '8884264435',
			infoTooltip: 'A consultation fee may apply.',
		},
	];
};

interface IReasonsForVisitListData {
	id: string;
	title: string;
	description: string;
}

export const reasonsForVisitListData: IReasonsForVisitListData[] = [
	{
		id: '1',
		title: 'Respiratory',
		description: 'Coughing, wheezing, sneezing, nasal discharge, nasal bleeding, difficulty breathing',
	},
	{
		id: '2',
		title: 'Gastrointestinal',
		description:
			'Not eating/drinking, vomiting, diarrhea, blood in vomit, blood in stool, black/tarry stool, constipation/struggling to defecate, abdominal pain, abdominal bloating, excessive hunger/thirst, anal gland issues (scooting, ruptured anal glands, abnormal smell from rectum), possible toxin ingestion',
	},
	{
		id: '3',
		title: 'Skin / Dermatological',
		description:
			'Rashes, itchiness, sores, hives, hair loss, toenail issues, wounds, fleas/ticks, skin growths/masses',
	},
	{
		id: '4',
		title: 'Genital / Urinary',
		description:
			'Straining to urinate, urinating in small amounts, urinating in inappropriate locations, abnormal urine quality (bloody, foul-smelling, cloudy), excessive urination, discharge from vulva/penis, issues with heat cycle, testicular problems (swelling, missing testicles, testicular growths)',
	},
	{
		id: '5',
		title: 'Muscular / Skeletal',
		description: 'Limping, arthritis, suspected fracture, unable to walk, muscle twitches/spasms, muscle atrophy',
	},
	{
		id: '6',
		title: 'Eyes / Ears',
		description:
			'Ear problems (red ears, painful ears, ear discharge, swollen ear flaps), eye issues (squinting, excessive tearing/eye discharge, red eyes, painful eyes, cloudy eyes, swollen eyes)',
	},
	{
		id: '7',
		title: 'Mouth / Dental',
		description:
			'Dental concerns (loose teeth, lost teeth, painful mouth, oral masses/lesions, trouble eating/chewing)',
	},
	{
		id: '8',
		title: 'Neurological',
		description:
			'Seizures or seizure-like activity, circling, head tilt, unsteady gait, unable to walk, dragging limbs, abnormal eye movements',
	},
	{
		id: '9',
		title: 'Behavioral',
		description: 'Anxiety, aggression, excessive barking, destructive behavior, phobias',
	},
	{
		id: '10',
		title: 'Other',
		description:
			'Lethargy, pain of unexplained source, bleeding, allergic reactions, trauma (hit by car, falls, dog bites)',
	},
];

export const createEncodedReasonList = (selectedReasons: string[]) => {
	if (!selectedReasons.length) {
		return [];
	}

	return selectedReasons.map((reason) => reasonsForVisitListData.find((r) => r.title === reason)?.id);
};

export const getSchema = (selectedReasonForVisit: ReasonForVisitType | null) =>
	z.object({
		reasonsForVisitBoolList: z
			.array(z.boolean())
			.refine((values) => selectedReasonForVisit === ReasonForVisitType.RoutineCheckUp || values.some(Boolean), {
				message: 'You must select at least one reason',
			}),
	});

export const getReasonsForVisitBoolList = (reasonsForVisitList: string[]) =>
	reasonsForVisitListData.map(({ title }) => reasonsForVisitList.includes(title));

export function hasTeletriageEnabled(loc?: Location | null): boolean {
	if (!loc) return false;
	const teletriageKey = CustomAttributeKeys?.teletriageEnabled ?? 'Teletriage Enabled';
	const teletriageValue = findCustomLocationAttribute(teletriageKey, loc);
	const normalizedValue = teletriageValue?.trim().toLowerCase();
	return normalizedValue === 'yes' || normalizedValue === 'true';
}
